import { useCallback } from 'react'
import type { ComparisonTab, GuidanceType } from '../components/ComparisonTabs'
import type { GridViewType } from '../components/GridViewToggle'
import { LinesFilterValue } from '../components/LinesFilter/LinesFilterDropdown'

const STORAGE_KEYS = {
  GRID_VIEW: 'decisionEditPage_gridView',
  IS_USD_SELECTED: 'decisionEditPage_isUSDSelected',
  REQUESTED_PRICE_ACTIVE_PL: 'decisionEditPage_requestedPriceActive_PL',
  REQUESTED_PRICE_ACTIVE_PN: 'decisionEditPage_requestedPriceActive_PN',
  GUIDANCE_SELECTIONS_PL: 'decisionEditPage_guidanceSelections_PL',
  GUIDANCE_SELECTIONS_PN: 'decisionEditPage_guidanceSelections_PN',
  LINES_FILTER: 'decisionEditPage_linesFilter',
} as const

const VALID_LINES_FILTER_VALUES: LinesFilterValue[] = ['all', 'open', 'my']

export const useDecisionPageStorage = () => {
  const getInitialTabs = useCallback((view: GridViewType): ComparisonTab[] => {
    const key =
      view === 'PL'
        ? STORAGE_KEYS.REQUESTED_PRICE_ACTIVE_PL
        : STORAGE_KEYS.REQUESTED_PRICE_ACTIVE_PN
    return localStorage.getItem(key) === 'true' ? ['requestedPrice'] : []
  }, [])

  const getInitialGuidanceSelections = useCallback((view: GridViewType): GuidanceType[] => {
    const key =
      view === 'PL' ? STORAGE_KEYS.GUIDANCE_SELECTIONS_PL : STORAGE_KEYS.GUIDANCE_SELECTIONS_PN
    try {
      const saved = localStorage.getItem(key)
      return saved ? (JSON.parse(saved) as GuidanceType[]) : []
    } catch {
      return []
    }
  }, [])

  const getInitialGridView = (): GridViewType => {
    const saved = localStorage.getItem(STORAGE_KEYS.GRID_VIEW)
    return saved === 'PL' || saved === 'PN' ? saved : 'PL'
  }

  const getInitialIsUSDSelected = (): boolean =>
    localStorage.getItem(STORAGE_KEYS.IS_USD_SELECTED) === 'true'

  const persistGridView = useCallback((view: GridViewType): void => {
    localStorage.setItem(STORAGE_KEYS.GRID_VIEW, view)
  }, [])

  const persistIsUSDSelected = useCallback((isUSD: boolean): void => {
    localStorage.setItem(STORAGE_KEYS.IS_USD_SELECTED, String(isUSD))
  }, [])

  const persistRequestedPriceTab = useCallback((view: GridViewType, isActive: boolean): void => {
    const key =
      view === 'PL'
        ? STORAGE_KEYS.REQUESTED_PRICE_ACTIVE_PL
        : STORAGE_KEYS.REQUESTED_PRICE_ACTIVE_PN
    localStorage.setItem(key, String(isActive))
  }, [])

  const persistGuidanceSelections = useCallback(
    (view: GridViewType, selections: GuidanceType[]): void => {
      const key =
        view === 'PL' ? STORAGE_KEYS.GUIDANCE_SELECTIONS_PL : STORAGE_KEYS.GUIDANCE_SELECTIONS_PN
      localStorage.setItem(key, JSON.stringify(selections))
    },
    []
  )

  const getInitialLinesFilter = (): LinesFilterValue | null => {
    const saved = localStorage.getItem(STORAGE_KEYS.LINES_FILTER)
    return VALID_LINES_FILTER_VALUES.includes(saved as LinesFilterValue)
      ? (saved as LinesFilterValue)
      : 'all'
  }

  const persistLinesFilter = useCallback((filter: LinesFilterValue | null): void => {
    if (filter === null) {
      localStorage.removeItem(STORAGE_KEYS.LINES_FILTER)
    } else {
      localStorage.setItem(STORAGE_KEYS.LINES_FILTER, filter)
    }
  }, [])

  const clearAllSelections = useCallback((): void => {
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key))
  }, [])

  return {
    STORAGE_KEYS,
    getInitialTabs,
    getInitialGuidanceSelections,
    persistGridView,
    persistIsUSDSelected,
    persistRequestedPriceTab,
    persistGuidanceSelections,
    persistLinesFilter,
    clearAllSelections,
    getInitialGridView,
    getInitialIsUSDSelected,
    getInitialLinesFilter,
  }
}
